// Small shared pieces for the AIPQ intro page
import { useState, useEffect, type CSSProperties, type ReactNode } from "react";
import type { AccentToken } from "../data";

export function Eyebrow({ children, accent }: { children: ReactNode; accent?: AccentToken }) {
  return (
    <div
      className="type-eyebrow"
      style={{
        color: accent ? `var(--aipq-${accent})` : "var(--aipq-text-muted)",
        margin: 0,
      }}
    >
      {children}
    </div>
  );
}

export function Icon({
  name,
  size = 18,
  style,
}: {
  name: string;
  size?: number;
  style?: CSSProperties;
}) {
  return (
    <span
      className="material-symbols-outlined"
      style={{ fontSize: size, fontVariationSettings: "'opsz' 20, 'wght' 300", ...style }}
    >
      {name}
    </span>
  );
}

export function HeroBackdrop() {
  return (
    <div aria-hidden className="hero-backdrop">
      <div className="hero-grid" />
      <div className="hero-glow hero-glow-1" />
      <div className="hero-glow hero-glow-2" />
    </div>
  );
}

export function TypingLine({
  text,
  speed = 28,
  delay = 0,
  cursor = true,
  onDone,
}: {
  text: string;
  speed?: number;
  delay?: number;
  cursor?: boolean;
  onDone?: () => void;
}) {
  const [out, setOut] = useState("");
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const s = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(s);
  }, [delay]);
  useEffect(() => {
    if (!started) return;
    if (out.length >= text.length) {
      onDone && onDone();
      return;
    }
    const t = setTimeout(() => setOut(text.slice(0, out.length + 1)), speed);
    return () => clearTimeout(t);
  }, [out, started, text, speed, onDone]);
  return (
    <span>
      {out}
      {cursor && started && out.length < text.length && <span className="caret">▍</span>}
    </span>
  );
}

export function Dots() {
  return (
    <span className="ast-dots">
      <span></span>
      <span></span>
      <span></span>
    </span>
  );
}

export function TopNav({
  onNav,
  active,
}: {
  onNav: (id: string) => void;
  active: string;
}) {
  const items = [
    { id: "who", label: "Philosophy" },
    { id: "where", label: "Where" },
  ];
  return (
    <header className="topnav-wrap">
      <div className="topnav-inner">
        <a
          className="topnav-brand"
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            onNav("top");
          }}
        >
          <span className="brand-mark">Q</span>
          <span className="brand-word">AIPQ</span>
        </a>
        <nav className="topnav-links">
          {items.map((it) => (
            <a
              key={it.id}
              href={`#${it.id}`}
              onClick={(e) => {
                e.preventDefault();
                onNav(it.id);
              }}
              className={active === it.id ? "is-active" : ""}
            >
              {it.label}
            </a>
          ))}
        </nav>
        <div className="topnav-right">
          <span className="kbd">aipq.kr</span>
          <a className="topnav-cta" href="https://aipq.kr" target="_blank" rel="noreferrer">
            Open chat ↗
          </a>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="footer-wrap">
      <div className="footer-inner">
        <span className="brand-mark small">Q</span>
        <span className="type-caption">© 2026 AIPQ · AI-native team</span>
        <div className="footer-spacer" />
        <a className="type-caption" href="mailto:contact@aipq.kr">
          contact@aipq.kr
        </a>
        <a className="type-caption" href="https://aipq.kr" target="_blank" rel="noreferrer">
          aipq.kr
        </a>
      </div>
    </footer>
  );
}
